import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { TextField, MenuItem, InputLabel, Box } from "@mui/material";
import dropdownIcon from "../../assets/images/frame.svg";

interface RHFSelectOutlinedProps {
  name: string;
  label?: string;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  defaultValue?: string;
  required?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  [key: string]: any;
}

export const RHFSelectOutlined = React.memo(({
  name,
  label,
  placeholder,
  options = [],
  defaultValue,
  required,
  onChange,
  children,
  SelectProps: customSelectProps,
  ...other
}: RHFSelectOutlinedProps) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue || ""}
      render={({ field, fieldState: { error } }) => (
        <Box sx={{ width: '100%' }}>
          {label && (
            <InputLabel
              sx={{
                color: '#222B45',
                fontFamily: 'Inter, sans-serif',
                fontSize: '15px',
                fontWeight: '400',
                marginBottom: '8px',
                display: 'block',
              }}
            >
              {label}
              {required && (
                <Box component="span" sx={{ color: '#FF0000', ml: 0.5 }}>
                  *
                </Box>
              )}
            </InputLabel>
          )}
          <TextField
            {...field}
            select
            error={!!error}
            helperText={error?.message}
            onChange={(e) => {
              field.onChange(e.target.value);
              if (onChange) {
                onChange(e as any);
              }
            }}
            SelectProps={{
              ...customSelectProps,
              displayEmpty: true,
              IconComponent: (props) => (
                <img
                  {...props}
                  src={dropdownIcon}
                  alt="dropdown icon"
                  style={{ width: 16, height: 16 }}
                />
              ),
              renderValue: (selected: any) => {
                if (!selected || selected === '') {
                  return <span style={{ color: '#A7AAB5' }}>{placeholder || 'Select...'}</span>;
                }
                const selectedOption = options.find(opt => opt.value === selected);
                return selectedOption ? selectedOption.label : selected;
              },
              MenuProps: {
                ...customSelectProps?.MenuProps,
                PaperProps: {
                  ...customSelectProps?.MenuProps?.PaperProps,
                  sx: {
                    maxHeight: 320,
                    ...customSelectProps?.MenuProps?.PaperProps?.sx,
                  },
                },
              },
            }}
            className='select-field'
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '40px',
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#C8CEE3 !important',
                  borderRadius: '8px',
                  borderWidth: '1px !important',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#C8CEE3 !important',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#C8CEE3 !important',
                  borderWidth: '1px !important',
                },
                '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#FF0000 !important',
                },
              },
              '& .MuiSelect-icon': {
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
              },
              '& .MuiOutlinedInput-input': {
                padding: '4px 14px',
                fontSize: '15px',
                color: '#4E556A',
              },
            }}
            fullWidth
            {...other}
          >
            {children || options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}
    />
  );
});

